import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Phone, Video, Mic, MicOff, VideoOff, Save, FileText, User as UserIcon, ArrowLeft, Loader, Paperclip } from 'lucide-react';
import { MedicalFilesPanel } from '../components/MedicalFilesPanel';
import { SessionTimer } from '../components/SessionTimer';
import { ConsultationPermissionsGate } from '../components/ConsultationPermissionsGate';
import { CallRecorder } from '../lib/callRecorder';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DoctorCallRoomPage = () => {
  const { callId } = useParams();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [call, setCall] = useState(null);
  const [agoraConfig, setAgoraConfig] = useState(null);
  const [error, setError] = useState('');
  const [callEnded, setCallEnded] = useState(false);
  const [mediaState, setMediaState] = useState({ mic: true, cam: false, remoteJoined: false });
  const [permissionsGranted, setPermissionsGranted] = useState(() => {
    try { return localStorage.getItem('tababa_permissions_granted') === 'true'; } catch { return false; }
  });

  const clientRef = useRef(null);
  const localTracksRef = useRef({ audio: null, video: null });
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const joinedChannelRef = useRef(false);
  const recorderRef = useRef(null);

  // Notes form state
  const [noteForm, setNoteForm] = useState({ medications: '', dosage: '', notes: '' });
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteMsg, setNoteMsg] = useState({ type: '', text: '' });
  const [savedNotes, setSavedNotes] = useState([]);

  // While the doctor is in a call, new callers land in the queue instead of
  // ringing. Surface them as a toast so nobody is left hanging.
  const waitingRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/calls/incoming`, { withCredentials: true });
        if (cancelled) return;
        const count = r.data?.waiting_count || 0;
        if (waitingRef.current !== null && count > waitingRef.current) {
          toast.info(
            t(`مريض ينتظر مكالمتك (${count} في الانتظار)`,
              `A patient is waiting for your call (${count} in queue)`,
              `Un patient attend votre appel (${count})`),
            { duration: 10000 },
          );
        }
        waitingRef.current = count;
      } catch { /* transient */ }
    };
    poll();
    const id = setInterval(poll, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [t]);


  useEffect(() => {
    if (!callId) return;
    let cancelled = false;

    const fetchAll = async () => {
      try {
        const [callRes, cfgRes] = await Promise.all([
          axios.get(`${API}/calls/status/${callId}`, { withCredentials: true }),
          axios.get(`${API}/config`)
        ]);
        if (cancelled) return;
        setCall(callRes.data);
        setAgoraConfig(cfgRes.data);
        setMediaState((s) => ({ ...s, cam: callRes.data.call_type === 'video' }));

        // If doctor needs to accept (status=waiting), accept it first
        if (callRes.data.status === 'waiting') {
          try {
            await axios.post(`${API}/calls/${callId}/accept`, {}, { withCredentials: true });
          } catch (e) {
            // Already active or someone else accepted — non-fatal, continue joining
            console.warn('call accept failed (likely already active):', e?.response?.status);
          }
        }

        // Fetch existing notes for this patient by this doctor
        try {
          const notesRes = await axios.get(`${API}/notes?patient_id=${callRes.data.patient_id}`, { withCredentials: true });
          setSavedNotes(notesRes.data);
        } catch (e) {
          // Notes load is optional — call can still proceed
          console.warn('notes load failed:', e?.response?.status);
        }
      } catch (err) {
        setError(t('فشل تحميل بيانات المكالمة', 'Failed to load call', 'Échec'));
      }
    };

    fetchAll();
    // If the doctor closes the tab, refreshes, or navigates away without
    // clicking End Call, still notify the backend so the call flips out of
    // "active" and doesn't get stuck. sendBeacon fires reliably during
    // page unload (unlike axios) and re-uses same-origin cookies for auth.
    const beforeUnload = () => {
      try {
        const url = `${API}/calls/${callId}/end`;
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([JSON.stringify({})], { type: 'application/json' }));
        }
      } catch (_e) { /* best effort */ }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      cancelled = true;
      window.removeEventListener('beforeunload', beforeUnload);
      cleanupAgora();
    };
  }, [callId]);

  // Join Agora once we have call data + config AND permissions granted
  useEffect(() => {
    if (!call || !agoraConfig || joinedChannelRef.current) return;
    if (callEnded) return;
    if (!permissionsGranted) return;
    joinedChannelRef.current = true;
    joinAgora(call.channel_name, call.call_type);
    // Notify counterpart that this participant has entered the room
    axios.post(`${API}/calls/${callId}/enter`, {}, { withCredentials: true }).catch(() => {});
  }, [call, agoraConfig, permissionsGranted]);

  const cleanupAgora = async () => {
    try {
      if (recorderRef.current) {
        try { await recorderRef.current.stop(); } catch (_) { /* ignore teardown errors */ }
        recorderRef.current = null;
      }
      if (localTracksRef.current.audio) localTracksRef.current.audio.close();
      if (localTracksRef.current.video) localTracksRef.current.video.close();
      if (clientRef.current) await clientRef.current.leave();
    } catch (e) {
      console.warn('Agora cleanup error:', e);
    }
    localTracksRef.current = { audio: null, video: null };
    clientRef.current = null;
  };

  const joinAgora = async (channelName, callType) => {
    if (!agoraConfig?.agora_enabled || !agoraConfig?.agora_app_id) return;
    if (clientRef.current) return;

    let client;
    try {
      const uid = Math.floor(Math.random() * 1000000);
      let tokRes;
      try {
        tokRes = await axios.get(`${API}/agora/token?channel=${channelName}&uid=${uid}`, { withCredentials: true });
      } catch (authErr) {
        if (authErr.response?.status === 401) {
          setError(t('انتهت جلستك', 'Session expired', 'Session expirée'));
          return;
        }
        throw authErr;
      }
      const { token, app_id } = tokRes.data;

      client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      clientRef.current = client;

      client.on('user-published', async (remoteUser, mediaType) => {
        await client.subscribe(remoteUser, mediaType);
        if (mediaType === 'video' && remoteVideoRef.current) {
          remoteUser.videoTrack.play(remoteVideoRef.current);
        }
        if (mediaType === 'audio') {
          remoteUser.audioTrack.play();
          // Feed remote audio into the recorder (key per remote uid so we don't double-add).
          try {
            const remoteTrack = remoteUser.audioTrack.getMediaStreamTrack();
            if (recorderRef.current && remoteTrack) {
              recorderRef.current.addTrack(`remote-${remoteUser.uid}`, remoteTrack);
            }
          } catch (err) { console.warn('recorder addTrack(remote) failed', err); }
        }
        setMediaState((s) => ({ ...s, remoteJoined: true }));
      });

      client.on('user-unpublished', (remoteUser) => {
        if (recorderRef.current) {
          recorderRef.current.removeTrack(`remote-${remoteUser.uid}`);
        }
        setMediaState((s) => ({ ...s, remoteJoined: false }));
      });

      // If the remote peer (patient) leaves the channel — for any reason
      // (hangup, network drop, browser close) — end the call server-side
      // so it doesn't sit forever in "active".
      client.on('user-left', () => {
        setMediaState((s) => ({ ...s, remoteJoined: false }));
        // Fire-and-forget; call is idempotent server-side.
        axios.post(`${API}/calls/${callId}/end`, {}, { withCredentials: true }).catch(() => {});
      });

      await client.join(app_id, channelName, token, uid);

      try {
        const audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
        localTracksRef.current.audio = audioTrack;
        const tracksToPublish = [audioTrack];

        if (callType === 'video') {
          const videoTrack = await AgoraRTC.createCameraVideoTrack();
          localTracksRef.current.video = videoTrack;
          tracksToPublish.push(videoTrack);
          if (localVideoRef.current) videoTrack.play(localVideoRef.current);
        }

        await client.publish(tracksToPublish);

        // Start MP3 recording (audio-only, even for video calls) once we have the local mic.
        try {
          const recorder = new CallRecorder();
          recorder.start();
          const localMicTrack = audioTrack.getMediaStreamTrack();
          if (localMicTrack) recorder.addTrack('local', localMicTrack);
          recorderRef.current = recorder;
        } catch (recErr) {
          console.warn('Call recording could not start:', recErr);
        }
      } catch (trackErr) {
        console.error('Local track error:', trackErr);
        setError(t('الميكروفون/الكاميرا غير متاحة', 'Microphone/camera unavailable', 'Micro/caméra indisponible'));
        try { await client.leave(); } catch (_) { /* ignore teardown errors */ }
        clientRef.current = null;
        joinedChannelRef.current = false;
      }
    } catch (e) {
      console.error('Agora join error:', e);
      setError(t('فشل الاتصال', 'Connection failed', 'Échec'));
      if (client) { try { await client.leave(); } catch (_) { /* ignore teardown errors */ } }
      clientRef.current = null;
      joinedChannelRef.current = false;
    }
  };

  const handleToggleMic = async () => {
    const tr = localTracksRef.current.audio;
    if (!tr) return;
    await tr.setEnabled(!mediaState.mic);
    setMediaState((s) => ({ ...s, mic: !s.mic }));
  };

  const handleToggleCam = async () => {
    const tr = localTracksRef.current.video;
    if (!tr) return;
    await tr.setEnabled(!mediaState.cam);
    setMediaState((s) => ({ ...s, cam: !s.cam }));
  };

  const handleEndCall = async () => {
    // 1. Stop recorder and grab the MP3 blob (audio-only) BEFORE we tear down Agora.
    let recordingBlob = null;
    let recordingDuration = 0;
    try {
      if (recorderRef.current) {
        const out = await recorderRef.current.stop();
        recorderRef.current = null;
        if (out && out.blob && out.blob.size > 0) {
          recordingBlob = out.blob;
          recordingDuration = out.durationSeconds || 0;
        }
      }
    } catch (recErr) {
      console.warn('Recorder stop failed:', recErr);
    }

    // 2. Mark the call ended on the backend.
    try {
      await axios.post(`${API}/calls/${callId}/end`, {}, { withCredentials: true });
    } catch (e) {
      console.warn('End call request failed (continuing with recording upload):', e?.message || e);
    }

    // 3. Upload the MP3 recording (best-effort; don't block end-of-call UX if it fails).
    if (recordingBlob) {
      try {
        const fd = new FormData();
        fd.append('file', recordingBlob, `call-${callId}.mp3`);
        await axios.post(
          `${API}/recordings/upload?call_id=${callId}&duration_seconds=${recordingDuration}`,
          fd,
          { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
        );
        toast.success(t('تم حفظ تسجيل المكالمة', 'Call recording saved', 'Enregistrement sauvegardé'));
      } catch (upErr) {
        console.warn('Recording upload failed:', upErr);
        toast.error(t('فشل رفع التسجيل', 'Failed to save recording', 'Échec de l\'enregistrement'));
      }
    }

    // 4. Tear down Agora and navigate.
    await cleanupAgora();
    setCallEnded(true);
    setTimeout(() => navigate('/doctor'), 1500);
  };

  // 5-minutes-left ping for psychology sessions — toast here + notify both
  // participants via the backend (idempotent).
  const handleSessionWarning = (remaining) => {
    const msg = remaining > 240
      ? t('تبقّى 5 دقائق على انتهاء الجلسة', '5 minutes left in this session', '5 minutes restantes')
      : t('الجلسة تقارب على الانتهاء', 'This session is about to end', 'La séance va se terminer');
    toast.warning(msg, { duration: 8000 });
    axios.post(`${API}/calls/${callId}/session-warning`, {}, { withCredentials: true }).catch(() => {});
  };

  // Regular consultations are capped at 3 minutes — close automatically.
  const handleTimeUp = async () => {
    toast.info(t('انتهت مدة الاستشارة (3 دقائق)', 'Consultation time is up (3 minutes)', 'Temps écoulé (3 min)'), { duration: 8000 });
    await handleEndCall();
  };



  const handleSaveNote = async (e) => {
    e.preventDefault();
    if (!call?.patient_id || !noteForm.notes.trim()) {
      setNoteMsg({ type: 'error', text: t('الرجاء كتابة الملاحظات', 'Please write notes', 'Veuillez écrire des notes') });
      return;
    }
    setNoteSaving(true);
    setNoteMsg({ type: '', text: '' });
    try {
      const res = await axios.post(`${API}/notes`, {
        patient_id: call.patient_id,
        medications: noteForm.medications,
        dosage: noteForm.dosage,
        notes: noteForm.notes,
        appointment_id: callId
      }, { withCredentials: true });
      setNoteMsg({ type: 'success', text: t('تم حفظ الملاحظة', 'Note saved', 'Note enregistrée') });
      setSavedNotes((prev) => [res.data, ...prev]);
      setNoteForm({ medications: '', dosage: '', notes: '' });
    } catch (err) {
      setNoteMsg({ type: 'error', text: err.response?.data?.detail || t('فشل الحفظ', 'Save failed', 'Échec') });
    } finally {
      setNoteSaving(false);
    }
  };

  if (!call) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        {error ? (
          <Alert variant="destructive" className="max-w-md"><AlertDescription>{error}</AlertDescription></Alert>
        ) : (
          <Loader className="animate-spin text-[#1e88e5]" size={48} />
        )}
      </div>
    );
  }

  const isVideo = call.call_type === 'video';
  // Privacy: for psychology sessions we hide the patient's real name on the
  // doctor's UI as well (see PsychologyJoinCallPage — patient hides doctor's
  // name symmetrically). Regular medical/pharmacy calls are unchanged.
  const isPsychology = call.category === 'psychology';
  const patientLabel = isPsychology
    ? t('المريض', 'Patient', 'Patient')
    : (call.patient_name || t('المريض', 'Patient', 'Patient'));

  // First-time permissions gate — camera / mic / notifications.
  if (!permissionsGranted) {
    return (
      <ConsultationPermissionsGate
        videoRequired={isVideo}
        onGranted={() => setPermissionsGranted(true)}
      />
    );
  }

  return (
    <div className={`min-h-screen bg-gray-100 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="container mx-auto px-4 py-4">
        <Button variant="ghost" onClick={() => navigate('/doctor')} className="mb-3" data-testid="back-to-doctor-dashboard">
          <ArrowLeft className={language === 'ar' ? 'ml-2 rotate-180' : 'mr-2'} size={20} />
          {t('العودة', 'Back', 'Retour')}
        </Button>

        {error && <Alert variant="destructive" className="mb-3"><AlertDescription>{error}</AlertDescription></Alert>}

        {callEnded ? (
          <Card className="p-8 text-center max-w-md mx-auto" data-testid="call-ended-doctor">
            <h2 className="text-2xl font-bold text-[#2c3e50] mb-2">{t('انتهت المكالمة', 'Call Ended', 'Terminé')}</h2>
            <p className="text-gray-600">{t('جاري العودة...', 'Returning...', 'Retour...')}</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="doctor-call-room">
            {/* LEFT: Call Pane */}
            <Card className="overflow-hidden flex flex-col" data-testid="call-pane">
              <div className="bg-[#1e88e5] text-white p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserIcon size={18} />
                  <div>
                    <p className="font-bold text-sm">{patientLabel}</p>
                    <p className="text-xs text-blue-100">
                      {isVideo ? t('مكالمة فيديو', 'Video Call', 'Vidéo') : t('مكالمة صوتية', 'Voice Call', 'Vocal')}
                      {' • '}
                      {mediaState.remoteJoined ? t('متصل', 'Connected', 'Connecté') : t('بانتظار المريض...', 'Waiting for patient...', 'En attente...')}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${mediaState.remoteJoined ? 'bg-green-500' : 'bg-yellow-500'}`}>
                  {mediaState.remoteJoined ? '● Live' : '○'}
                </span>
              </div>

              {isPsychology ? (
                <div className="bg-[#0d47a1] text-white px-3 py-2 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold">
                    {t('مدة الجلسة', 'Session length', 'Durée')}
                  </span>
                  <SessionTimer
                    elapsedSeconds={call.session_elapsed_seconds}
                    minutes={call.session_minutes}
                    onWarning={handleSessionWarning}
                  />
                </div>
              ) : (
                <div className="bg-[#0d47a1] text-white px-3 py-2 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold">
                    {t('مدة الاستشارة', 'Consultation time', 'Durée')}
                  </span>
                  <SessionTimer
                    elapsedSeconds={call.session_elapsed_seconds}
                    minutes={call.session_minutes}
                    onExpire={handleTimeUp}
                  />
                </div>
              )}

              {isVideo ? (
                <div className="relative bg-gray-900 flex-1 min-h-[300px] lg:min-h-[420px]">
                  <div ref={remoteVideoRef} className="w-full h-full flex items-center justify-center text-white">
                    {!mediaState.remoteJoined && (
                      <div className="text-center">
                        <div className="relative inline-flex w-20 h-20 mb-3">
                          <div className="absolute inset-0 bg-white/20 rounded-full animate-ping"></div>
                          <div className="relative w-20 h-20 bg-[#1e88e5] rounded-full flex items-center justify-center">
                            <Video size={40} />
                          </div>
                        </div>
                        <p>{t('بانتظار المريض...', 'Waiting for patient...', 'En attente du patient...')}</p>
                      </div>
                    )}
                  </div>
                  <div ref={localVideoRef} className="absolute bottom-3 right-3 w-28 h-20 bg-black rounded-lg overflow-hidden border-2 border-white"></div>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-[#1565c0] to-[#1e88e5] py-12 text-center text-white flex-1 min-h-[300px] lg:min-h-[420px] flex flex-col justify-center">
                  <div className="relative inline-flex w-28 h-28 mb-3 mx-auto">
                    <div className="absolute inset-0 bg-white/20 rounded-full animate-ping"></div>
                    <div className="relative w-28 h-28 bg-white/30 backdrop-blur rounded-full flex items-center justify-center">
                      <Phone size={48} />
                    </div>
                  </div>
                  <p className="text-xl font-bold mb-1">{patientLabel}</p>
                  <p className="text-blue-100 text-sm">
                    {mediaState.remoteJoined ? t('متصل', 'Connected', 'Connecté') : t('بانتظار المريض...', 'Waiting for patient...', 'En attente...')}
                  </p>
                </div>
              )}

              <div className="p-3 flex justify-center gap-2 bg-gray-50 border-t">
                <Button onClick={handleToggleMic} variant={mediaState.mic ? 'outline' : 'destructive'} size="lg" className="rounded-full w-12 h-12 p-0" data-testid="doctor-toggle-mic">
                  {mediaState.mic ? <Mic size={18} /> : <MicOff size={18} />}
                </Button>
                {isVideo && (
                  <Button onClick={handleToggleCam} variant={mediaState.cam ? 'outline' : 'destructive'} size="lg" className="rounded-full w-12 h-12 p-0" data-testid="doctor-toggle-cam">
                    {mediaState.cam ? <Video size={18} /> : <VideoOff size={18} />}
                  </Button>
                )}
                <Button onClick={handleEndCall} variant="destructive" size="lg" className="rounded-full w-12 h-12 p-0" data-testid="doctor-end-call">
                  <Phone size={18} className="rotate-[135deg]" />
                </Button>
              </div>
            </Card>

            {/* RIGHT: Notes pane */}
            <Card className="flex flex-col" data-testid="notes-pane">
              <div className="bg-[#2c3e50] text-white p-3 flex items-center gap-2">
                <FileText size={18} />
                <h3 className="font-bold text-sm">{t('الملاحظات والوصفة', 'Notes & Prescription', 'Notes & Ordonnance')}</h3>
              </div>

              <div className="p-4 flex-1 overflow-y-auto max-h-[500px]">
                {/* Patient symptom summary (read-only) */}
                {call.medical_form?.current_symptoms && (
                  <div className="bg-blue-50 rounded-lg p-3 mb-4 text-sm">
                    <p className="font-semibold text-[#2c3e50] mb-1">{t('أعراض المريض', 'Patient Symptoms', 'Symptômes')}</p>
                    <p className="text-gray-700">{call.medical_form.current_symptoms}</p>
                    {call.medical_form.additional_notes && (
                      <p className="text-gray-700 mt-1"><strong>{t('ملاحظات:', 'Notes:', 'Notes:')}</strong> {call.medical_form.additional_notes}</p>
                    )}
                    {call.medical_form.allergies && <p className="text-gray-700 mt-1"><strong>{t('الحساسية:', 'Allergies:', 'Allergies:')}</strong> {call.medical_form.allergies}</p>}
                  </div>
                )}

                {/* Patient files (live during the call) — polls every 4s; toasts on new file */}
                {call.patient_id && (
                  <div className="mb-4 pb-4 border-b" data-testid="doctor-patient-files">
                    <MedicalFilesPanel
                      patientId={call.patient_id}
                      readOnly
                      pollInterval={4000}
                      onNewFile={(f) => {
                        toast.success(
                          t(
                            `ملف جديد من المريض: ${f.original_filename}`,
                            `New file from patient: ${f.original_filename}`,
                            `Nouveau fichier du patient : ${f.original_filename}`
                          ),
                          {
                            icon: <Paperclip size={16} />,
                            duration: 6000,
                            position: 'top-center',
                            description: f.description || undefined,
                          }
                        );
                      }}
                    />
                  </div>
                )}

                {noteMsg.text && (
                  <Alert variant={noteMsg.type === 'error' ? 'destructive' : 'default'} className={`mb-3 ${noteMsg.type === 'success' ? 'bg-green-50 border-green-200' : ''}`}>
                    <AlertDescription className={noteMsg.type === 'success' ? 'text-green-800' : ''}>{noteMsg.text}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSaveNote} className="space-y-3" data-testid="doctor-note-form">
                  <div>
                    <Label className="text-sm">{t('الأدوية', 'Medications', 'Médicaments')}</Label>
                    <Input value={noteForm.medications} onChange={(e) => setNoteForm({ ...noteForm, medications: e.target.value })} placeholder={t('مثلاً: باراسيتامول', 'e.g. Paracetamol', 'ex: Paracétamol')} className="mt-1" data-testid="room-medications" />
                  </div>
                  <div>
                    <Label className="text-sm">{t('الجرعة', 'Dosage', 'Posologie')}</Label>
                    <Input value={noteForm.dosage} onChange={(e) => setNoteForm({ ...noteForm, dosage: e.target.value })} placeholder={t('مثلاً: 500mg مرتين يومياً', 'e.g. 500mg twice daily', 'ex: 500mg 2x/jour')} className="mt-1" data-testid="room-dosage" />
                  </div>
                  <div>
                    <Label className="text-sm">{t('الملاحظات', 'Notes', 'Notes')} <span className="text-red-500">*</span></Label>
                    <Textarea value={noteForm.notes} onChange={(e) => setNoteForm({ ...noteForm, notes: e.target.value })} required rows={5} className="mt-1" placeholder={t('اكتب الملاحظات أثناء المكالمة...', 'Write notes during the call...', 'Écrivez pendant l\'appel...')} data-testid="room-notes" />
                  </div>
                  <Button type="submit" disabled={noteSaving} className="w-full bg-[#1e88e5] hover:bg-[#1565c0]" data-testid="room-save-note">
                    <Save className={language === 'ar' ? 'ml-2' : 'mr-2'} size={16} />
                    {noteSaving ? t('جاري الحفظ...', 'Saving...', 'Enregistrement...') : t('حفظ الملاحظة', 'Save Note', 'Enregistrer')}
                  </Button>
                </form>

                {savedNotes.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs text-gray-500 mb-2 uppercase font-semibold">{t('ملاحظات سابقة لهذا المريض', 'Previous notes for this patient', 'Notes précédentes')}</p>
                    <div className="space-y-2">
                      {savedNotes.slice(0, 3).map((n) => (
                        <div key={n._id} className="text-xs bg-gray-50 rounded p-2">
                          <p className="text-gray-500 mb-1">{new Date(n.created_at).toLocaleString()}</p>
                          {n.medications && <p><strong>{t('الأدوية:', 'Meds:', 'Méd:')}</strong> {n.medications}</p>}
                          {n.dosage && <p><strong>{t('الجرعة:', 'Dose:', 'Dose:')}</strong> {n.dosage}</p>}
                          <p className="text-gray-700 line-clamp-2">{n.notes}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};
